import React, { useEffect, useRef, useState } from 'react';
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { Card, CardBody, CardHeader, Col, Row } from "react-bootstrap";
import { useParams, useNavigate, Link } from "react-router-dom";
import Button from "../common/Button/Button.jsx"
import { createAssignBatch, updateAssignBatch, getAssignBatchById, getBatches, getStudents } from '../../../services/BatchAssignModule.js';
// import { createAssignBatch, updateAssignBatch, getAssignBatchById, getBatches, getStudents } from '../../../../services/BatchModule.js';
import { useAuth } from '../../../context/AuthContext';
import { toast } from '../common/Toast/toast';
// import AsyncSelectDropdown from '../../common/FormFields/AysncSelectDropdown.jsx';
import AsyncSelectDropdown from '../common/CustomAsyncDropdown.jsx';

function AssignBatch() {
  const { id } = useParams(); // Get assignment ID from URL for edit mode
  const navigate = useNavigate();
  const isEditMode = Boolean(id);
  
  const [initialValues, setInitialValues] = useState({
    batchId: "",
    studentIds: [], // Array for multiple students
  });

  // Options and selected state for batch and students
  const [batchOptions, setBatchOptions] = useState([]);
  const [studentOptions, setStudentOptions] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [selectedStudents, setSelectedStudents] = useState([]); // Array for multiselect
  const [loading, setLoading] = useState(false);

  const validationSchema = Yup.object({
    batchId: Yup.string().required("Batch selection is required"),
    studentIds: Yup.array()
      .min(1, "At least one student must be selected")
      .required("Student selection is required"),
  });

  const hasShownToast = useRef(false);
  const { hasPermission } = useAuth();

  // Permissions guard
  useEffect(() => {
    if (!hasPermission('batch-assignment', 'view', 'lms')) {
      if (!hasShownToast.current) {
        toast.error('You do not have permission to assign batches.');
        navigate('/');
        hasShownToast.current = true;
      }
    }
  }, [hasPermission, navigate]);

  // Fetch assignment data when in edit mode
  useEffect(() => {
    if (isEditMode) {
      fetchAssignmentData();
    }
  }, [id]);

  const fetchAssignmentData = async () => {
    try {
      setLoading(true);
      const response = await getAssignBatchById(id);
      const assignmentData = response.data;
      
      setInitialValues({
        batchId: assignmentData.batchId || "",
        studentIds: assignmentData.studentIds || [],
      });
    } catch (error) {
      toast.error("Failed to fetch assignment data");
      navigate('/batches-assignments');
    } finally {
      setLoading(false);
    }
  };

  // Fetch batches and students from API
  useEffect(() => {
    const loadOptions = async () => {
      try {
        const [batchesResponse, studentsResponse] = await Promise.all([
          getBatches(),
          getStudents(),
        ]);

        // console.log("Batches Response:", batchesResponse); // Debug log
        // console.log("Students Response:", studentsResponse); // Debug log

        // Map batches to { label, value } pairs
        const batchData = batchesResponse.data || [];
        const batchOpts = batchData.map((batch) => ({
          label: batch?.name ?? batch?.code ?? String(batch?.id ?? ''),
          value: String(batch?.id ?? batch?.code ?? ''),
        }));

        // Map students to { label, value } pairs with "name | email" format
        const studentData = studentsResponse.data || [];
        const studentOpts = studentData.map((student) => ({
          label: `${student?.name ?? 'Unknown'} | ${student?.email ?? 'No email'}`,
          value: String(student?.id ?? ''),
          studentData: student, // Keep original data for reference
        }));

        // console.log("Batch Options:", batchOpts); // Debug log
        // console.log("Student Options:", studentOpts); // Debug log

        setBatchOptions(batchOpts);
        setStudentOptions(studentOpts);
      } catch (error) {
        console.error("Failed to load batches/students", error);
        toast.error("Failed to load batch and student data");
      }
    };

    loadOptions();
  }, []);

  // Sync selected dropdown values from initialValues (edit mode)
  useEffect(() => {
    if (initialValues.batchId && batchOptions.length > 0) {
      const selectedBatchOption = batchOptions.find(
        option => option.value === initialValues.batchId
      );
      setSelectedBatch(selectedBatchOption || null);
    } else {
      setSelectedBatch(null);
    }

    if (initialValues.studentIds.length > 0 && studentOptions.length > 0) {
      const selectedStudentOptions = studentOptions.filter(
        option => initialValues.studentIds.includes(option.value)
      );
      setSelectedStudents(selectedStudentOptions);
    } else {
      setSelectedStudents([]);
    }
  }, [initialValues.batchId, initialValues.studentIds, batchOptions, studentOptions]);

  const handleSubmit = async (values, { resetForm, setSubmitting }) => {
    try {
      let response;
      if (isEditMode) {
        response = await updateAssignBatch(id, values);
        toast.success("Batch assignment updated successfully");
      } else {
        response = await createAssignBatch(values);
        toast.success("Students assigned to batch successfully");
        resetForm();
        // Clear selections on create success
        setSelectedBatch(null);
        setSelectedStudents([]);
      }

      navigate('/batches-assignments');
    } catch (error) {
      console.error(`Failed to ${isEditMode ? 'update' : 'create'} batch assignment:`, error);
      toast.error(`Failed to ${isEditMode ? 'update' : 'create'} batch assignment`);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Card className="pt-4">
        <CardBody className="text-center">
          <div>Loading assignment data...</div>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card className="pt-4">
      <CardHeader>
        <h4 className="text-center mb-0">
          {isEditMode ? 'Edit Batch Assignment' : 'Assign Students to Batch'}
        </h4>
        <Link to={"/batches-assignments"}>
          <Button
            label="Back to list"
            type="button"
            variant="primary"
            className="submitButton customButton w-30"
          />
        </Link>
      </CardHeader>
      
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize={true}
      >
        {({ isSubmitting, setFieldValue, errors, touched }) => (
          <CardBody>
            <Form>
              <Row>
                {/* Batch Selection */}
                <Col lg={12}>
                  <label className="form-label">Select Batch *</label>
                  <AsyncSelectDropdown
                    options={batchOptions}
                    value={selectedBatch}
                    onChange={(opt) => {
                      setSelectedBatch(opt || null);
                      setFieldValue("batchId", opt ? opt.value : "");
                    }}
                    placeholder="Select a batch"
                    isMulti={false}
                  />
                  {touched.batchId && errors.batchId ? (
                    <div className="text-danger small mt-1">{errors.batchId}</div>
                  ) : null}
                </Col>
              </Row>

              <Row className="mt-3">
                {/* Student Selection (Multi-select) */}
                <Col lg={12}>
                  <label className="form-label">Select Students *</label>
                  <AsyncSelectDropdown
                    options={studentOptions}
                    value={selectedStudents}
                    onChange={(opts) => {
                      const selectedOptions = opts || [];
                      setSelectedStudents(selectedOptions);
                      const studentIds = selectedOptions.map(opt => opt.value);
                      setFieldValue("studentIds", studentIds);
                    }}
                    placeholder="Select students to assign to this batch"
                    isMulti={true}
                  />
                  {touched.studentIds && errors.studentIds ? (
                    <div className="text-danger small mt-1">{errors.studentIds}</div>
                  ) : null}
                  <small className="text-muted">
                    Students are displayed as: Name | Email
                  </small>
                </Col>
              </Row>

              {/* Selected Summary */}
              {selectedBatch && selectedStudents.length > 0 && (
                <Row className="mt-4">
                  <Col lg={12}>
                    <div className="alert alert-info">
                      <h6>Assignment Summary:</h6>
                      <p><strong>Batch:</strong> {selectedBatch.label}</p>
                      <p><strong>Students ({selectedStudents.length}):</strong></p>
                      <ul className="mb-0">
                        {selectedStudents.map((student, index) => (
                          <li key={index}>{student.label}</li>
                        ))}
                      </ul>
                    </div>
                  </Col>
                </Row>
              )}

              <div className="d-flex justify-content-center mt-4">
                <Button
                  type="submit"
                  label={isEditMode ? 'Update Assignment' : 'Assign Students'}
                  variant="primary"
                  className="submitButton customButton w-30"
                  disabled={isSubmitting}
                />
              </div>
            </Form>
          </CardBody>
        )}
      </Formik>
    </Card>
  );
}

export default AssignBatch;
